import { useState } from "react";
import { Trophy, Medal, Award, Crown, Sparkles, Flame } from "lucide-react";

import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import type { LeaderboardAgent } from "../types/types";

interface HeroPodiumProps {
  topAgents: LeaderboardAgent[];
  selectedMetric?: string; // Add selected metric prop to display correct value
  searchQuery?: string; // Optional search query for filtering display
}

export const HeroPodium = function({ topAgents, selectedMetric = 'total-premium', searchQuery }: HeroPodiumProps) {
  // Hidden easter egg: each podium finisher gets a secret accessory that only
  // pops up when you click their avatar. Keyed by rank (1/2/3).
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  // Per-rank secret accessory: icon + how it sits on the avatar (angled, like it's
  // actually being worn). #1 crown (gold, tilted left), #2 sparkles (silver, right),
  // #3 flame (bronze, right).
  const getAccessory = (rank: number): { Icon: typeof Crown; cls: string } | null => {
    switch (rank) {
      case 1:
        return { Icon: Crown, cls: "-top-3 -left-3 h-7 w-7 -rotate-[35deg] text-yellow-500 fill-yellow-400" };
      case 2:
        return { Icon: Sparkles, cls: "-top-3 -right-3 h-6 w-6 rotate-[20deg] text-slate-400 fill-slate-300" };
      case 3:
        return { Icon: Flame, cls: "-top-3 -right-2 h-6 w-6 rotate-[12deg] text-amber-600 fill-amber-500" };
      default:
        return null;
    }
  };

  // Only agents with something on the board for the selected metric. With one
  // producer the podium used to show "#2 — $0" and "#3 — $0" (Mark, 09-18).
  const metricNumber = (agent: LeaderboardAgent): number => {
    switch (selectedMetric) {
      case 'total-premium': return agent.totalPremium || 0;
      case 'total-policies': return agent.totalPolicies || agent.totalSales || 0;
      case 'appointments': return agent.appointments || 0;
      case 'calls-made': return agent.callsMade || 0;
      case 'conversion-rate': return agent.conversionRate || 0;
      case 'goal-progress': return agent.goalProgress || 0;
      default: return (agent as { issuedPremium?: number }).issuedPremium || 0;
    }
  };
  const podiumAgents = topAgents.filter((a) => metricNumber(a) > 0).slice(0, 3);

  // If we don't have at least 1 agent, don't render anything
  if (podiumAgents.length === 0) {
    return null;
  }

  // Helper function to get metric value
  const getMetricValue = (agent: LeaderboardAgent): string => {
    switch (selectedMetric) {
      case 'total-premium-sold':
        return `$${((agent as { issuedPremium?: number }).issuedPremium || 0).toLocaleString()}`;
      case 'total-premium':
        return `$${agent.totalPremium.toLocaleString()}`;
      case 'total-policies':
        return (agent.totalPolicies || agent.totalSales || 0).toString();
      case 'appointments':
        return agent.appointments.toString();
      case 'calls-made':
        return agent.callsMade.toString();
      case 'conversion-rate':
        return `${(agent.conversionRate || 0).toFixed(1)}%`;
      case 'goal-progress':
        return agent.goalProgress == null ? '—' : `${agent.goalProgress.toFixed(0)}%`;
      default:
        return `$${((agent as { issuedPremium?: number }).issuedPremium || 0).toLocaleString()}`;
    }
  };

  // Helper function to get metric label
  const getMetricLabel = (): string => {
    switch (selectedMetric) {
      case 'total-premium-sold':
        return 'Premium Sold';
      case 'total-premium':
        return 'Total Production';
      case 'total-policies':
        return 'Policies Sold';
      case 'appointments':
        return 'Appointments';
      case 'calls-made':
        return 'Calls Made';
      case 'conversion-rate':
        return 'Closing Ratio';
      case 'goal-progress':
        return 'of goal';
      default:
        return 'Premium Sold';
    }
  };

  // Helper function to get metric name for header
  const getMetricDisplayName = (): string => {
    switch (selectedMetric) {
      case 'total-premium-sold':
        return 'premium sold';
      case 'total-premium':
        return 'production';
      case 'total-policies':
        return 'policies';
      case 'appointments':
        return 'appointment';
      case 'calls-made':
        return 'calls';
      case 'conversion-rate':
        return 'conversion';
      case 'goal-progress':
        return 'goal progress';
      default:
        return 'premium';
    }
  };

  const getPodiumIcon = (actualRank: number) => {
    switch (actualRank) {
      case 1:
        return Trophy;
      case 2:
        return Medal;
      case 3:
        return Award;
      default:
        return Trophy;
    }
  };

  const getPodiumColors = (actualRank: number) => {
    switch (actualRank) {
      case 1:
        return {
          bg: "bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600",
          text: "text-yellow-900",
          border: "border-yellow-300",
          height: "h-32",
        };
      case 2:
        return {
          bg: "bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500",
          text: "text-gray-900",
          border: "border-gray-300",
          height: "h-28",
        };
      case 3:
        return {
          bg: "bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800",
          text: "text-amber-100",
          border: "border-amber-500",
          height: "h-24",
        };
      default:
        return {
          bg: "bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700",
          text: "text-blue-100",
          border: "border-blue-300",
          height: "h-20",
        };
    }
  };

  // Create a function to render a single agent
  const renderAgent = (agent: LeaderboardAgent, displayIndex: number) => {
    // Use the display rank (position in sorted list) for the current metric
    // displayIndex is 0-based, so add 1 to get rank (1, 2, 3)
    const displayRank = displayIndex + 1;
    const colors = getPodiumColors(displayRank);
    const Icon = getPodiumIcon(displayRank);
    const accessory = getAccessory(displayRank);
    const AccessoryIcon = accessory?.Icon ?? Crown;
    const isRevealed = !!revealed[displayRank];

    return (
      <div key={`agent-${agent.userId}-${displayRank}`} className="flex flex-col items-center">
        {/* Agent Card */}
        <Card
          className={`w-48 mb-4 border-2 ${colors.border} shadow-lg hover:shadow-xl transition-shadow`}
        >
          <CardContent className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              {/* Wrapper sized to the avatar so the secret accessory positions
                  relative to the head (top corner), not the whole card. */}
              <div className="relative">
                {accessory && (
                  <AccessoryIcon
                    className={`absolute z-10 drop-shadow-md origin-center transition-all duration-300 ease-out ${accessory.cls} ${
                      isRevealed ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none"
                    }`}
                    aria-hidden={!isRevealed}
                  />
                )}
                <Avatar
                  onClick={accessory ? () => setRevealed((r) => ({ ...r, [displayRank]: !r[displayRank] })) : undefined}
                  key={`avatar-${agent.userId}-${agent.profileImageUrl || agent.profile_image_url || 'no-image'}`}
                  className={`w-16 h-16 border-4 border-white shadow-lg ${accessory ? 'cursor-pointer select-none' : ''}`}
                >
                  <AvatarImage
                    src={agent.profileImageUrl || agent.profile_image_url || undefined}
                    alt={agent.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-xl font-bold bg-primary text-primary-foreground">
                    {getInitialsFromFullName(agent.name.split(" ")[0] || "U", agent.name.split(" ")[1] || "N")}
                  </AvatarFallback>
                </Avatar>
              </div>
            </div>

            {/* Reserve a consistent 2-line height so a long name (which wraps)
                doesn't make one card taller than the others and break the podium
                staircase. Name is centered within the reserved space. */}
            <div className="min-h-[3.5rem] flex items-center justify-center mb-1">
              <h3 className="font-bold text-lg line-clamp-2">{agent.name}</h3>
            </div>

            <div className="space-y-2">
              <div className="text-2xl font-bold text-green-600">
                {getMetricValue(agent)}
              </div>
              <div className="text-sm text-muted-foreground">
                {getMetricLabel()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Podium Base */}
        <div
          className={`${colors.bg} ${colors.height} w-32 rounded-t-lg border-2 ${colors.border} flex flex-col items-center justify-center shadow-lg`}
        >
          <Icon className={`h-8 w-8 ${colors.text} mb-2`} />
          <span className={`text-2xl font-bold ${colors.text}`}>
            #{displayRank}
          </span>
        </div>
      </div>
    );
  };

  // Arrange for traditional podium display
  // Position 1 (center), Position 2 (left), Position 3 (right)
  // The agents are already sorted by the selected metric, so use their array position
  const podiumOrder: (LeaderboardAgent | null)[] = [null, null, null];

  // Take up to 3 agents and assign podium positions based on array position (already sorted)
  if (podiumAgents.length >= 1) podiumOrder[1] = podiumAgents[0]; // 1st place (index 0) goes to center
  if (podiumAgents.length >= 2) podiumOrder[0] = podiumAgents[1]; // 2nd place (index 1) goes to left
  if (podiumAgents.length >= 3) podiumOrder[2] = podiumAgents[2]; // 3rd place (index 2) goes to right

  // If we have no agents, show empty state
  if (podiumAgents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🏆</div>
        <h2 className="text-2xl font-bold text-gray-600 mb-2">Hall of Champions</h2>
        <p className="text-gray-500">
          No top performers available for the current selection.
        </p>
      </div>
    );
  }

  return (
    <div className="relative" data-tour="leaderboard-podium">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
          🏆 Hall of Champions
        </h2>
        <p className="text-muted-foreground mt-2">
          Top {getMetricDisplayName()} performers this period
        </p>
      </div>

      <div className="flex items-end justify-center gap-4 mb-8">
        {podiumOrder.map((agent, podiumIndex) => {
          if (!agent) return null;
          // Use podium position to determine display rank (0=2nd/left, 1=1st/center, 2=3rd/right)
          // Map podium positions back to ranks: center=1, left=2, right=3
          const displayRank = podiumIndex === 1 ? 1 : podiumIndex === 0 ? 2 : 3;
          return renderAgent(agent, displayRank - 1);
        })}
      </div>
    </div>
  );
};